import React from "react";
import { Button } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

const RCC_SOURCE_KEY = "skygeni-rcc-source";

const useStyles = makeStyles((theme) => ({
  backButton: {
    marginBottom: theme.spacing(1),
    textTransform: "none",
    fontWeight: 500,
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: "rgba(38, 97, 173, 0.08)",
    },
  },
}));

const NAV_MAP = {
  "Enhanced Insights": "enhancedPage",
  "Rep Performance": "repPage",
  "Other Insights": "othersPage",
  "Marketing Support Requests": "marketPage",
  "Growth Modeling": "growthModelingPage",
  "Pipeline Build & Sufficiency": "pipelineBuildandSuficencyPage",
  "Pipeline Velocity & Conversion": "pipelineVelocityandConversionPage",
  "Market Intel & Growth Levers": "marketIntelandGrowthLeversPage",
  "Sales Productivity": "salesProductivityPage",
  "Growth Science Insights": "growthScienceInsightsPage",
  "Custom Scorecards": "personalizedScorecardsPage",
  Settings: "SettingsPage",
  "Product Performance": "productPage",
};

/**
 * Check if the user navigated from Revenue Command Center to the current page
 * @returns {boolean}
 */
export const isFromRcc = () => {
  try {
    const sourceData = sessionStorage.getItem(RCC_SOURCE_KEY);
    if (!sourceData) return false;

    // Parse stored source data
    const { group, dashboard } = JSON.parse(sourceData);

    // Get current page state
    const currentGroup = localStorage.getItem("skygeni-user-page");

    // Check if we are in the correct group
    if (!currentGroup || currentGroup !== group) return false;

    // Check if we are on the correct dashboard within the group
    const dashboardKey = NAV_MAP[currentGroup];
    const currentDashboard = localStorage.getItem(
      `skygeni-user-${dashboardKey}`,
    );
    return currentDashboard === dashboard;
  } catch (e) {
    // If parsing fails or data is invalid, assume not from RCC
    return false;
  }
};

/**
 * Clear the RCC source marker
 */
export const clearRccSource = () => {
  sessionStorage.removeItem(RCC_SOURCE_KEY);
};

/**
 * Back to Revenue Command Center button component.
 * Only renders if user came from RCC (sessionStorage marker exists).
 */
const BackToRcc = () => {
  const classes = useStyles();
  const history = useHistory();
  const user = useSelector((state) => state.app?.user);

  const shouldShow = isFromRcc();
  if (!shouldShow) {
    if (sessionStorage.getItem(RCC_SOURCE_KEY)) {
      clearRccSource();
    }
    return null;
  }

  const handleBackToRcc = () => {
    // Clear the marker first
    clearRccSource();

    const elements = user?.elements || [];
    const rccGroupObj = elements.find(
      (group) =>
        group?.charts &&
        group.charts.some((c) => c?.Chart_Name === "Revenue Command Center"),
    );
    const rccGroup = rccGroupObj?.navItem;

    if (rccGroup === "Home") {
      localStorage.setItem("skygeni-user-page", "Home");
      Object.values(NAV_MAP).forEach((key) => {
        localStorage.removeItem(`skygeni-user-${key}`);
      });
    } else if (rccGroup && NAV_MAP[rccGroup]) {
      localStorage.setItem("skygeni-user-page", rccGroup);
      localStorage.setItem(
        `skygeni-user-${NAV_MAP[rccGroup]}`,
        "Revenue Command Center",
      );
    } else {
      localStorage.setItem("skygeni-user-page", "Other Insights");
      localStorage.setItem("skygeni-user-othersPage", "Revenue Command Center");
    }

    if (rccGroup && NAV_MAP[rccGroup]) {
      const targetKey = NAV_MAP[rccGroup];
      Object.values(NAV_MAP).forEach((key) => {
        if (key !== targetKey) {
          localStorage.removeItem(`skygeni-user-${key}`);
        }
      });
    }

    // Navigate and reload (same pattern as RccCard)
    history.push("/");
    window.location.reload();
  };

  return (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={handleBackToRcc}
      className={classes.backButton}
      data-test="back-to-rcc-button"
    >
      Back to Revenue Command Center
    </Button>
  );
};

export default BackToRcc;
